import React, { useState, useEffect } from "react";
import { Button, Modal } from "flowbite-react";
import { IoMdAdd } from "react-icons/io";
import Select from "react-select";
import ModuleCard from "../ModuleCard";
import ConfigureModuleDays from "./ConfigureModuleDays";
import {
  useGetAllModulesQuery,
  useAddModuleToClusterMutation,
} from "../../../../graphql/generated.tsx";
import { gql, useQuery } from "@apollo/client";
import { toast } from "../../../../toast";
import Loader from "../../../../components/Loader.jsx";

// ✅ Hardcoded getModulesByCluster query
const GET_MODULES_BY_CLUSTER = gql`
  query GetModulesByCluster($campusName: String!, $clusterId: String!) {
    getModulesByCluster(campusName: $campusName, clusterId: $clusterId) {
      moduleID
      enabled_dates
    }
  }
`;

const ManageModuleTab = ({ campusName, clusterId }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedModules, setSelectedModules] = useState([]);
  const [moduleToConfigure, setModuleToConfigure] = useState(null);
  const [Isload, setIsLoad] = useState(true);

  const [addModuleToCluster, { loading: addingModule }] =
    useAddModuleToClusterMutation({
      refetchQueries: ["GetModulesByCluster"],
      onCompleted: () => {
        setModuleToConfigure(null);
      },
      onError: (error) => {
        console.error("Error adding module to cluster:", error);
        toast.error("❌ Failed to add module. Please try again.");
      },
    });

  const {
    data: clusterData,
    loading: clusterLoading,
    error: clusterError,
  } = useQuery(GET_MODULES_BY_CLUSTER, {
    variables: { campusName, clusterId },
    fetchPolicy: "network-only",
    skip: !campusName || !clusterId,
  });

  const {
    data: moduleData,
    loading: modulesLoading,
    error: modulesError,
  } = useGetAllModulesQuery({
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoad(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const allModules = moduleData?.getAllModules || [];
  const clusterModules = clusterData?.getModulesByCluster || [];

  const mergedClusterModules = clusterModules.map((cm) => {
    const fullModule = allModules.find((am) => am._id === cm.moduleID);
    return {
      ...fullModule,
      moduleID: cm.moduleID,
      enabled_dates: cm.enabled_dates || [],
    };
  });

  const toggleModal = () => setShowModal(!showModal);

  const moduleOptions = modulesLoading
    ? []
    : allModules.map((m) => ({
        label: m.name,
        value: m._id,
        description: m.short_description,
      }));

  const handleAddSelectedModules = () => {
    if (selectedModules.length > 0) {
      const moduleToAdd = allModules.find(
        (m) => m._id === selectedModules[0].value
      );
      if (moduleToAdd) {
        setModuleToConfigure(moduleToAdd);
      }
    }
    setShowModal(false);
  };

  const handleSaveConfiguration = async (configuredModule) => {
    try {
      if (!campusName || !clusterId) {
        console.error("Missing required parameters:", { campusName, clusterId });
        return;
      }

      const enabledDates = (configuredModule.enabled_dates || []).map((date) =>
        date instanceof Date ? date.toISOString() : date
      );

      if (enabledDates.length === 0) {
        enabledDates.push(new Date().toISOString());
      }

      const finalPayload = {
        campusName: String(campusName).trim(),
        clusterId: String(clusterId).trim(),
        module: {
          moduleID: String(configuredModule._id),
          enabled_dates: enabledDates,
        },
      };

      await addModuleToCluster({
        variables: finalPayload,
      });

      toast.success(`Module "${configuredModule.name}" added successfully!`, {
        position: "top-right",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error saving module configuration:", error);
      toast.error("❌ Failed to save module configuration.");
      setModuleToConfigure(null);
    }
  };

  if (moduleToConfigure) {
    return (
      <ConfigureModuleDays
        module={moduleToConfigure}
        onSave={handleSaveConfiguration}
        onCancel={() => setModuleToConfigure(null)}
      />
    );
  }

  return (
    <>
      <div className="p-10">
        <div className="flex items-end justify-end mb-8">
          <Button
            onClick={toggleModal}
            className="flex items-center p-0 text-white rounded-lg shadow-md bg-primary"
          >
            <IoMdAdd className="mt-1 mr-1" /> <span>Add Module</span>
          </Button>
        </div>

        {/* Loading State */}
        {(modulesLoading || clusterLoading || Isload) && (
          <div className="flex items-center justify-center py-8">
            <Loader />
          </div>
        )}

        {/* Error State */}
        {(modulesError || clusterError) && (
          <div className="py-4 text-center text-red-500">
            Error loading modules. Please try again.
          </div>
        )}

        {/* Display Modules or No Modules Message */}
        {!modulesLoading && !clusterLoading && !Isload && (
          <>
            {mergedClusterModules.length === 0 ? (
              <div className="flex justify-center items-center h-40 text-gray-500 text-lg font-medium">
                No Modules Found
              </div>
            ) : (
              <div className="flex flex-wrap gap-6 max-md:p-5">
                {mergedClusterModules.map((module) => (
                  <ModuleCard
                    key={module.moduleID}
                    moduleId={module.moduleID}
                    clusterId={clusterId}
                    campusName={campusName}
                    name={module.name}
                    short_description={module.short_description}
                    domain={module.domain}
                    start_date={module.enabled_dates?.[0] || null}
                    end_date={
                      module.enabled_dates?.[module.enabled_dates.length - 1] ||
                      null
                    }
                    daywise_plan={module.daywise_plan}
                    onEdit={() => setModuleToConfigure(module)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <Modal show={showModal} onClose={toggleModal}>
        <Modal.Header>Add Modules to Cluster</Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            <p className="text-base leading-relaxed text-gray-500">
              Select modules from the list to assign them to this cluster.
            </p>
            {modulesLoading ? (
              <div className="flex justify-center py-4">
                <Loader />
              </div>
            ) : modulesError ? (
              <p className="text-red-500">
                Error loading modules: {modulesError.message}
              </p>
            ) : (
              <Select
                isMulti
                options={moduleOptions}
                onChange={setSelectedModules}
                placeholder="Search and select modules..."
                isSearchable={true}
                isClearable={true}
                noOptionsMessage={() => "No modules found"}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleAddSelectedModules}>Add Selected</Button>
          <Button color="gray" onClick={toggleModal}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ManageModuleTab;
